import {React, ui} from 'lib';

import {useRemoteData, useSingleQueryValueAsNumber, useSingleQueryValueAsTrue, BoolCheckboxInput, LayoutItem} from 'shared';

import {Action} from 'types/elcstockbook/api/data.gen';
import {StaffFrame, useStore, StaffSelect} from './base';
import {ActionDetailDialog} from './action/detail';
import {useModelDetailDialog, Model} from './model';
import {useQueryValue, ManagedFilter} from './model_filter';
import {Waiting, WaitingCard, NotifyActionDialog} from './waiting_card';

const masterPath = 'staff/get_master_data';

const getWaitingsPath = 'staff/get_waitings';


export function WaitingPage(): React.ReactElement {
    const actionId = useSingleQueryValueAsNumber('action');
    const detailDisclosure = ui.useDisclosure({
        isOpen: actionId.value !== null,
        onClose: () => actionId.replace(null),
    });

    const waitingsRef = React.useRef<Ref<typeof Waitings>>(null);

    const modelDetailDialog = useModelDetailDialog({
        onUpdated: (model) => {
            waitingsRef.current?.updateModel(model);
        },
    });

    return <StaffFrame
        title="入荷連絡"
        hideFloatingContent={modelDetailDialog.isOpen}
    >
        <Waitings ref={waitingsRef} />
        <ActionDetailDialog
            id={actionId.value}
            onUpdated={(action) => {
                waitingsRef.current?.updateAction(action);
            }}
            {...detailDisclosure}
        />
        {modelDetailDialog.dialog}
    </StaffFrame>;
};



const Waitings = React.forwardRef<{
    updateAction(action: Action): void;
    updateModel(model: Model): void;
}, {}>((_props, ref) => {
    const store = useStore();
    const actionId = useSingleQueryValueAsNumber('action');
    const staffId = useSingleQueryValueAsNumber('staff');
    const desc = useSingleQueryValueAsTrue('desc');

    const {data, reload, setData} = useRemoteData({
        path: getWaitingsPath,
        request: {
            store_id: store.id,
            staff_id: staffId.value,
            ascending: !desc.value,
            ...useQueryValue(),
        }
    });

    const {data: master} = useRemoteData({
        path: masterPath,
        request: {
            store_id: store.id,
        },
    });

    React.useImperativeHandle(ref, () => ({
        updateAction: (action: Action) => {
            setData((old) => old.map(w => ({
                ...w,
                action: w.action.id === action.id ? action : w.action,
            })));
        },
        updateModel: (model: Model) => {
            setData((old) => old.map(w => {
                if (w.detail.item.product.model.id === model.id) {
                    const clone = {...w};
                    clone.detail.item.product.model = model;
                    return clone;
                }
                return w;
            }));
        },
    }));

    const [editing, setEditing] = React.useState<Waiting>();
    const disclosure = ui.useDisclosure({
        isOpen: editing !== undefined,
        onClose: () => setEditing(undefined),
    });

    return <ui.Box my={10}>
        <LayoutItem>
            <ui.Heading>
                入荷連絡
            </ui.Heading>
        </LayoutItem>

        {master && <LayoutItem>
            <ui.Stack spacing={5}>
                <ManagedFilter
                    master={master}
                    showGenderFilter={false}
                    showSeasonFilter={false}
                    showCategoryFilter={false}
                    showLineFilter={false}
                    showColorFilter={false}
                    showSizeFilter={false}
                    showSaleFilter={false}
                />

                <StaffSelect
                    value={staffId.value}
                    onChange={(_, v) => staffId.push(v)}
                />

                <BoolCheckboxInput
                    value={desc.value ?? false}
                    onChange={(_, v) => desc.push(v || null)}
                    label="新しい入荷連絡を先頭に表示"
                />
            </ui.Stack>
        </LayoutItem>}

        {data?.length === 0 && <LayoutItem>
            <ui.Text color="gray.500">
                条件に一致する入荷連絡はありません
            </ui.Text>
        </LayoutItem>}

        {data?.map((wait) => <LayoutItem key={wait.id}>
            <WaitingCard
                waiting={wait}
                onOpenAction={(id) => actionId.push(id)}
                onNotify={setEditing}
            />
        </LayoutItem>)}

        <NotifyActionDialog
            waiting={editing}
            onComplete={() => {
                disclosure.onClose();
                reload();
            }}
            {...disclosure}
        />
    </ui.Box>;
});
